// Piezas chicas de la app «Deals»: glifo, chip de etapa, chip de seguimiento,
// selector «Mover a…» (alternativa a arrastrar para teclado y pantallas táctiles).

import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { ArrowRightLeft, Briefcase, Check } from 'lucide-react';

import { FOLLOW_UP, followUpLabel, stageColor } from '../model';
import type { DealStage, FollowUpRow } from '../types';

export const stageStyle = (s?: Pick<DealStage, 'color' | 'kind'>): CSSProperties => ({ ['--dl-stage' as any]: stageColor(s) });

export function DealsGlyph({ size = 36 }: { size?: number }) {
  return (
    <span aria-hidden style={{ width: size, height: size }} className="dl-glyph grid shrink-0 place-items-center rounded-xl">
      <Briefcase style={{ width: size * 0.52, height: size * 0.52 }} strokeWidth={2.2} />
    </span>
  );
}

export function StageChip({ stage, name, className = '' }: { stage?: DealStage; name?: string; className?: string }) {
  return (
    <span style={stageStyle(stage)}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted px-2 py-0.5 text-[11.5px] font-semibold text-foreground ${className}`}>
      <span className="dl-dot h-2 w-2 rounded-full" aria-hidden />
      {stage?.name || name || '—'}
    </span>
  );
}

export function FollowUpChip({ row, className = '' }: { row?: FollowUpRow; className?: string }) {
  if (!row) return null;
  const t = FOLLOW_UP[row.follow_up_state] || FOLLOW_UP.sin_fecha;
  const label = followUpLabel(row.follow_up_state, row.next_contact_date, row.days_overdue);
  if (!label) return null;
  return (
    <span className={`${t.tone} dl-chip inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${className}`}>
      {label}
    </span>
  );
}

/** «Mover a…»: menú con las etapas del tablero (teclado: ↑/↓, Enter, Esc). */
export function MoveMenu({ stages, current, onMove, label = 'Mover a…', compact, disabled }: {
  stages: DealStage[]; current: number; onMove: (s: DealStage) => void; label?: string; compact?: boolean; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc, true);
    window.setTimeout(() => ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus(), 0);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc, true); };
  }, [open]);
  const onMenuKey = (e: KeyboardEvent) => {
    const items = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') || []);
    const i = items.findIndex((b) => b === document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
  };
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}
        title={label} disabled={disabled}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 ${compact ? 'h-9 w-9 justify-center' : 'h-9 px-3 text-[13px] font-medium'}`}>
        <ArrowRightLeft className="h-4 w-4" aria-hidden />
        {compact ? <span className="sr-only">{label}</span> : label}
      </button>
      {open && (
        <div id={id} role="menu" aria-label="Mover a la etapa" onKeyDown={onMenuKey}
          className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          {stages.map((s) => (
            <button key={s.id} type="button" role="menuitem" disabled={s.id === current} style={stageStyle(s)}
              onClick={() => { setOpen(false); onMove(s); }}
              className="flex min-h-[40px] w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13.5px] hover:bg-muted disabled:cursor-default disabled:opacity-60">
              <span className="dl-dot h-2.5 w-2.5 rounded-full" aria-hidden />
              <span className="flex-1">{s.name}</span>
              {s.id === current && <Check className="h-4 w-4" aria-label="actual" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Avatar del responsable. El backend aún no envía su nombre (solo
 *  `owner_user_id`), así que se muestra un avatar neutro con rótulo honesto. */
export function OwnerAvatar({ ownerId, mine, size = 26 }: { ownerId: number | null; mine?: boolean; size?: number }) {
  const label = ownerId === null ? 'Sin responsable' : mine ? 'Tuyo' : `Responsable #${ownerId}`;
  return (
    <span title={label} aria-label={label} role="img" style={{ width: size, height: size }}
      className={`grid shrink-0 place-items-center rounded-full text-[10.5px] font-semibold ${ownerId === null ? 'border border-dashed border-border text-muted-foreground' : 'dl-accent-soft dl-accent-text'}`}>
      {ownerId === null ? '–' : mine ? 'Tú' : `#${ownerId}`.slice(0, 4)}
    </span>
  );
}
